import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, batch } from 'react-redux';
import moment from 'moment-timezone';
import { store } from '../../../redux_setup/store';
import {
  setIsDragging,
  setDragSource,
  setDraggedItem,
  setIsCloneDragging,
  setCalendarItems,
  updateCalendarItemsOrder,
  setPlaceholderInfo,
  setDragIsInProgress,
  setDroppableContainerCurrentlyHoveredByDraggedItem,
  moveExerciseBetweenDatesMultiEnabled,
  setOnBeforeCapture,
  setActiveModal,
  setSelectedSavedWorkout,
  setSelectedDate,
  addPreSavedItemToCalendarItemsArray,
  setMonthSelectionActiveDate,
  setSelectedItems,
} from '../ducks/calendarActions';
import {
  getSessionBreakChunk,
  isIndexInsideCollapsedSessionBreak,
  getIndexAfterSessionBreakGroup,
  wasUnderCollapsedSessionBreak,
} from './dragSessionBreakHelpers';
import generateUniqueId from '../../../shared/utils/generateUniqueId';
import { buildMonthViewExpandedMap } from '../utils/sessionBreakExpansion';

const MONTH_DAY_PREFIX = 'month-day-';

const isMonthDay = (id) => typeof id === 'string' && id.startsWith(MONTH_DAY_PREFIX);
const dateFromMonthDayId = (id) => id.slice(MONTH_DAY_PREFIX.length);

/** Sidebar droppable ids whose items can be dragged onto a month-day cell.
 * Mirrors the list in useDragManager (day/week view) so behavior matches. */
const SIDEBAR_SOURCES = [
  'liftCalendarItems', 'warmUpCalendarItems', 'SAQCCalendarItems',
  'circuitCalendarItems', 'noteCalendarItems', 'questionnaireCalendarItems',
  'coolDownCalendarItems', 'healthCalendarItems', 'progressionCalendarItems',
  'tagCalendarItems', 'accessoryCalendarItems',
];

const getDayItems = (state, dateKey) => state.calendar.data.calendarItems[dateKey]?.exercises || [];

/**
 * Drag manager for the calendar month view. Lives alongside (not inside)
 * `useDragManager` so day/week behavior stays untouched. Scope:
 *  - same-day reorder within a month-day cell
 *  - cross-day move between two month-day cells
 *  - session break chunk movement (single chunk only)
 *
 * Sidebar → month-day drop adds a preSave copy of the dragged exercise to
 * the target day (see `handleSidebarDrop`), mirroring day/week view.
 * preSave items added this way (or in week view) reach this manager via the
 * in-grid paths and are skipped from the persistence calls below.
 *
 * Out of scope: combine/superset, multi-select, free-form-program.
 */
const useMonthViewDragManager = () => {
  const dispatch = useDispatch();

  const cleanupDragState = useCallback(() => {
    dispatch(setDragIsInProgress(false));
    dispatch(setPlaceholderInfo(null));
    dispatch(setDroppableContainerCurrentlyHoveredByDraggedItem(null));
    dispatch(setOnBeforeCapture(null));
    dispatch(setIsCloneDragging(false));
    dispatch(setIsDragging(false));
    dispatch(setDragSource(null));
    dispatch(setDraggedItem(null));
  }, [dispatch]);

  const handleSameDayReorder = useCallback((result, accountCode) => {
    const { source, destination } = result;
    const dateKey = dateFromMonthDayId(source.droppableId);
    const state = store.getState();
    const originalItems = Array.from(getDayItems(state, dateKey));
    /** Month view collapses session breaks of its own now (the chevron in
     * ReadOnlyExerciseItem), so the drop helpers have to see the same
     * collapsed breaks the coach does — and only those. */
    const expandedItems = buildMonthViewExpandedMap(
      state.calendar.data.expandedItems,
      originalItems,
    );
    const currentItem = originalItems[source.index];
    if (!currentItem) return;

    /** Multi-select group reorder. When the grabbed row is part of a
     * day-scoped month selection of >1, move the whole selected chunk
     * together. The other selected rows collapse to zero height BEFORE rbd
     * captures dimensions (see handleOnBeforeCapture), so rbd's geometry is
     * clean and `destination.index` is a true full-array index that still
     * counts the hidden selected rows. We therefore mirror the week/day
     * multi-select math: a visible-neighbor no-op guard plus an index
     * adjustment that subtracts the selected rows sitting before the drop
     * point (otherwise the chunk lands N rows too low — the "scooped hidden
     * rows" bug). */
    const selectedItems = state.calendar.data.selectedItems || {};
    const selectedItemIds = Object.keys(selectedItems);
    if (selectedItems[currentItem.assignedId] && selectedItemIds.length > 1) {
      const draggedIndex = source.index;
      const destinationIndex = destination.index;

      let prevVisibleIndex = null;
      for (let i = draggedIndex - 1; i >= 0; i -= 1) {
        if (!selectedItemIds.includes(String(originalItems[i].assignedId))) {
          prevVisibleIndex = i;
          break;
        }
      }
      let nextVisibleIndex = null;
      for (let i = draggedIndex + 1; i < originalItems.length; i += 1) {
        if (!selectedItemIds.includes(String(originalItems[i].assignedId))) {
          nextVisibleIndex = i;
          break;
        }
      }

      /** No-op guard: the drop stayed within the hidden selected-rows gap
       * (didn't cross a visible row) — leave the order untouched. */
      const movedDownOnlyAcrossHidden = destinationIndex > draggedIndex
        && (nextVisibleIndex === null || destinationIndex < nextVisibleIndex);
      const movedUpOnlyAcrossHidden = destinationIndex < draggedIndex
        && (prevVisibleIndex === null || destinationIndex > prevVisibleIndex);
      if (movedDownOnlyAcrossHidden || movedUpOnlyAcrossHidden) return;

      const selectedItemsChunk = originalItems.filter(
        (item) => selectedItemIds.includes(String(item.assignedId)),
      );
      const numberOfSelectedItemsBeforeDestinationIndex = originalItems
        .slice(0, destination.index)
        .filter((item) => selectedItemIds.includes(String(item.assignedId)))
        .length;
      const filteredItems = originalItems.filter(
        (item) => !selectedItemIds.includes(String(item.assignedId)),
      );

      const draggedItemIsBeforeDestination = source.index < destination.index;
      let adjustedDestinationIndex = destination.index
        - numberOfSelectedItemsBeforeDestinationIndex
        + (draggedItemIsBeforeDestination ? 1 : 0);

      /** Dropping into the hidden gap while moving down: land just after
       * the nearest visible row above the gap. */
      const destinationItem = originalItems[destination.index];
      const destinationIsSelected = destinationItem
        && selectedItemIds.includes(String(destinationItem.assignedId));
      if (destinationIsSelected && destination.index > draggedIndex
        && !isIndexInsideCollapsedSessionBreak(originalItems, destination.index, expandedItems)) {
        let lastVisibleIndexBeforeDestination = -1;
        for (let i = destination.index - 1; i >= 0; i -= 1) {
          if (!selectedItemIds.includes(String(originalItems[i].assignedId))) {
            lastVisibleIndexBeforeDestination = i;
            break;
          }
        }
        adjustedDestinationIndex = lastVisibleIndexBeforeDestination >= 0
          ? filteredItems.findIndex(
            (item) => item.assignedId
              === originalItems[lastVisibleIndexBeforeDestination].assignedId,
          ) + 1
          : 0;
      }

      /** Don't drop inside a collapsed break's gap (inert in month). */
      if (isIndexInsideCollapsedSessionBreak(
        filteredItems,
        adjustedDestinationIndex,
        expandedItems,
      )) {
        adjustedDestinationIndex = getIndexAfterSessionBreakGroup(
          filteredItems,
          adjustedDestinationIndex,
        );
      }
      adjustedDestinationIndex = Math.max(
        0,
        Math.min(adjustedDestinationIndex, filteredItems.length),
      );

      filteredItems.splice(adjustedDestinationIndex, 0, ...selectedItemsChunk);

      dispatch(setCalendarItems(filteredItems, dateKey));

      const order = filteredItems
        .map((it) => it.assignedId)
        .filter((id) => typeof id !== 'string');
      if (!order.length) return;

      const movingIds = selectedItemsChunk
        .map((it) => it.assignedId)
        .filter((id) => typeof id !== 'string');

      dispatch(updateCalendarItemsOrder(accountCode, {
        date: dateKey,
        order,
        assignedIdsBeingMoved: movingIds,
        movedToIndex: order.indexOf(movingIds[0]),
      }, originalItems));
      return;
    }

    /** Session-break chunk extraction (mirrors useDragManager same-day path). */
    const sessionBreakChunk = currentItem.type === 'session_break'
      ? getSessionBreakChunk(originalItems, source.index)
      : [];
    const chunk = sessionBreakChunk.length ? sessionBreakChunk : [currentItem];

    const filteredItems = originalItems.filter((item) => !chunk.includes(item));

    let insertIndex = destination.index;
    if (sessionBreakChunk.length && destination.index > source.index) {
      /** rbd's destination.index already accounts for the dragged item
       * being removed, but it doesn't know we're also moving the rest
       * of the session-break chunk along with it. When dropping below
       * the original position, subtract (chunkLength - 1) so the chunk
       * lands where the user expects instead of overshooting by the
       * size of the trailing chunk items. */
      insertIndex = insertIndex - (sessionBreakChunk.length - 1);
    }

    /** Don't drop a single item into the visual gap of a collapsed
     * session break — slide to the end of that group instead. */
    if (chunk.length === 1
      && isIndexInsideCollapsedSessionBreak(filteredItems, insertIndex, expandedItems)) {
      insertIndex = getIndexAfterSessionBreakGroup(filteredItems, insertIndex);
    }

    filteredItems.splice(insertIndex, 0, ...chunk);

    /** Scoop-prevention: after splicing a session-break chunk, we may have
     * pulled in items hidden under a different collapsed break at the
     * destination. Move those above the dragged break. */
    let finalItems = filteredItems;
    if (sessionBreakChunk.length) {
      const newChunkAfterInsert = getSessionBreakChunk(filteredItems, insertIndex);
      const unintentionallyMovedItems = newChunkAfterInsert.filter((newItem) => {
        const wasInOriginal = sessionBreakChunk.some(
          (orig) => orig.assignedId === newItem.assignedId,
        );
        return !wasInOriginal
          && wasUnderCollapsedSessionBreak(newItem, expandedItems, originalItems);
      });

      if (unintentionallyMovedItems.length) {
        const movedIds = unintentionallyMovedItems.map((i) => String(i.assignedId));
        const stripped = filteredItems.filter((i) => !movedIds.includes(String(i.assignedId)));
        const draggedIndex = stripped.findIndex(
          (item) => item.assignedId === currentItem.assignedId,
        );
        if (draggedIndex !== -1) {
          stripped.splice(draggedIndex, 0, ...unintentionallyMovedItems);
        }
        finalItems = stripped;
      }
    }

    /** Always commit the local reorder up front so the UI reflects the
     * drop even when no saved items remain (e.g. a day made up entirely
     * of preSave items dragged in from week view). The persistence call
     * below is what depends on numeric assignedIds. */
    dispatch(setCalendarItems(finalItems, dateKey));

    /** Build ordered assignedIds (preSave items have string ids — exclude). */
    const newOrderedAssignmentIds = finalItems
      .map((item) => item.assignedId)
      .filter((id) => typeof id !== 'string');

    if (!newOrderedAssignmentIds.length) return;

    const filteredAssignedIdsBeingMoved = chunk
      .map((item) => item.assignedId)
      .filter((id) => typeof id !== 'string');

    const submissionObject = {
      date: dateKey,
      order: newOrderedAssignmentIds,
      assignedIdsBeingMoved: filteredAssignedIdsBeingMoved,
      movedToIndex: newOrderedAssignmentIds.indexOf(filteredAssignedIdsBeingMoved[0]),
    };

    dispatch(updateCalendarItemsOrder(accountCode, submissionObject, originalItems));
  }, [dispatch]);

  const handleCrossDayMove = useCallback((result, accountCode) => {
    const { source, destination } = result;
    const sourceDate = dateFromMonthDayId(source.droppableId);
    const destDate = dateFromMonthDayId(destination.droppableId);
    const state = store.getState();

    const startItems = Array.from(getDayItems(state, sourceDate));
    const endItems = Array.from(getDayItems(state, destDate));
    const expandedItems = buildMonthViewExpandedMap(
      state.calendar.data.expandedItems,
      startItems,
      endItems,
    );
    const removed = startItems[source.index];
    if (!removed) return;

    /** Active day always follows the drop: every cross-day branch below
     * (single item, session-break chunk, multi-select chunk) lands the
     * items in destDate, so the highlight + action bar move there too.
     * The per-branch .catch() handlers reset to sourceDate if the network
     * move fails and the items revert. */
    dispatch(setMonthSelectionActiveDate(destDate));

    const selectedItems = state.calendar.data.selectedItems || {};
    const selectedItemIds = Object.keys(selectedItems);
    const draggedIsSelected = !!selectedItems[removed.assignedId];
    /** Dragging an item that isn't part of the multi-selection abandons that
     * selection — clear it so stale blue rows don't linger on the now-inactive
     * day (matches plain-click / day-cell-click behavior). When the dragged
     * item Is selected, the selection legitimately follows to destDate, so keep
     * it (covers both the 2+ chunk move and the single shift-selected item). */
    if (!draggedIsSelected && selectedItemIds.length) {
      dispatch(setSelectedItems({}));
    }

    /** Multi-select group move across days. The selection is day-scoped,
     * so every selected id lives in the source day. Pull the whole chunk
     * out of the source day and drop it contiguously into the dest day,
     * then persist with the multi-enabled change-date endpoint (same one
     * the cross-day session-break chunk path uses below). */
    if (selectedItems[removed.assignedId] && selectedItemIds.length > 1) {
      const chunk = startItems.filter(
        (item) => selectedItemIds.includes(String(item.assignedId)),
      );
      const newStartItems = startItems.filter(
        (item) => !selectedItemIds.includes(String(item.assignedId)),
      );

      let insertIndex = destination.index;
      if (isIndexInsideCollapsedSessionBreak(endItems, insertIndex, expandedItems)) {
        insertIndex = getIndexAfterSessionBreakGroup(endItems, insertIndex);
      }
      const newEndItems = [...endItems];
      newEndItems.splice(insertIndex, 0, ...chunk);

      dispatch(setCalendarItems(newStartItems, sourceDate));
      dispatch(setCalendarItems(newEndItems, destDate));

      const chunkAssignedIds = chunk
        .map((item) => item.assignedId)
        .filter((id) => typeof id !== 'string');
      if (!chunkAssignedIds.length) return;

      dispatch(moveExerciseBetweenDatesMultiEnabled(
        accountCode,
        chunkAssignedIds,
        destDate,
        sourceDate,
        destDate,
        newEndItems,
      )).catch(() => {
        dispatch(setCalendarItems(startItems, sourceDate));
        dispatch(setCalendarItems(endItems, destDate));
        dispatch(setMonthSelectionActiveDate(sourceDate));
      });
      return;
    }

    const sessionBreakChunk = removed.type === 'session_break'
      ? getSessionBreakChunk(startItems, source.index)
      : [];

    if (sessionBreakChunk.length > 1) {
      /** Cross-day session break chunk move (mirrors useDragManager
       * cross-date branch line ~929). */
      const newStartItems = startItems.filter(
        (item) => !sessionBreakChunk.some((sb) => sb.assignedId === item.assignedId),
      );
      const newEndItems = [...endItems];
      newEndItems.splice(destination.index, 0, ...sessionBreakChunk);

      const newChunkAfterInsert = getSessionBreakChunk(newEndItems, destination.index);
      const unintentionallyMovedItems = newChunkAfterInsert.filter((newItem) => {
        const wasInOriginal = sessionBreakChunk.some(
          (orig) => orig.assignedId === newItem.assignedId,
        );
        return !wasInOriginal
          && wasUnderCollapsedSessionBreak(newItem, expandedItems, endItems);
      });

      let finalEndItems = newEndItems;
      if (unintentionallyMovedItems.length) {
        const movedIds = unintentionallyMovedItems.map((i) => String(i.assignedId));
        const stripped = newEndItems.filter((i) => !movedIds.includes(String(i.assignedId)));
        const draggedIndex = stripped.findIndex(
          (item) => item.assignedId === removed.assignedId,
        );
        if (draggedIndex !== -1) {
          stripped.splice(draggedIndex, 0, ...unintentionallyMovedItems);
        }
        finalEndItems = stripped;
      }

      dispatch(setCalendarItems(newStartItems, sourceDate));
      dispatch(setCalendarItems(finalEndItems, destDate));

      const filteredAssignedIds = sessionBreakChunk
        .map((i) => i.assignedId)
        .filter((id) => typeof id !== 'string');
      if (!filteredAssignedIds.length) return;

      /** Pass `finalEndItems` (post-scoop-prevention) so the post-move
       * reorder payload computed inside the thunk matches what we just
       * dispatched to redux. Passing `newEndItems` here would persist
       * the un-corrected order and the UI would snap to it on refresh. */
      dispatch(moveExerciseBetweenDatesMultiEnabled(
        accountCode,
        filteredAssignedIds,
        destDate,
        sourceDate,
        destDate,
        finalEndItems,
      )).catch(() => {
        dispatch(setCalendarItems(startItems, sourceDate));
        dispatch(setCalendarItems(endItems, destDate));
        dispatch(setMonthSelectionActiveDate(sourceDate));
      });
      return;
    }

    /** Single-item cross-day move. */
    let adjustedDestinationIndex = destination.index;
    if (isIndexInsideCollapsedSessionBreak(endItems, destination.index, expandedItems)) {
      adjustedDestinationIndex = getIndexAfterSessionBreakGroup(endItems, destination.index);
    }

    const newStartItems = [...startItems];
    newStartItems.splice(source.index, 1);
    const newEndItems = [...endItems];
    newEndItems.splice(adjustedDestinationIndex, 0, removed);

    dispatch(setCalendarItems(newStartItems, sourceDate));
    dispatch(setCalendarItems(newEndItems, destDate));

    /** preSave items (added in week view but not yet saved) live only in
     * redux — skip the network call and let the visual move stand. */
    if (removed.preSave) return;
    if (typeof removed.assignedId === 'string') return;

    dispatch(moveExerciseBetweenDatesMultiEnabled(
      accountCode,
      [removed.assignedId],
      destDate,
      sourceDate,
      destDate,
      newEndItems,
    )).catch(() => {
      dispatch(setCalendarItems(startItems, sourceDate));
      dispatch(setCalendarItems(endItems, destDate));
      dispatch(setMonthSelectionActiveDate(sourceDate));
    });
  }, [dispatch]);

  /** Sidebar exercise dragged onto a month-day cell: build a preSave copy
   * of the source item and insert it into the target day. Mirrors the
   * day/week sidebar branch in useDragManager (~line 841). */
  const handleSidebarDrop = useCallback((result) => {
    const { source, destination, draggableId } = result;
    const dateKey = dateFromMonthDayId(destination.droppableId);
    const state = store.getState();

    const sourceItems = state.calendar.data.calendarItemContainers?.[source.droppableId] || [];
    const idMinusSideBarFlag = draggableId.match(/^(.*)-sideBar$/i)
      ? draggableId.match(/^(.*)-sideBar$/i)[1]
      : null;
    const removed = sourceItems.find((item) => item.id === idMinusSideBarFlag);
    if (!removed) return;

    const copiedItem = {
      ...removed,
      title: removed.name,
      exerciseId: parseInt(removed.id, 10),
      id: `${removed.id}-${generateUniqueId()}`,
      assignedId: `${removed.id}-${generateUniqueId()}`,
      preSave: true,
    };

    dispatch(addPreSavedItemToCalendarItemsArray(
      copiedItem,
      dateKey,
      destination.index,
      destination.droppableId,
    ));
  }, [dispatch]);

  const finishDragEnd = useCallback((result) => {
    cleanupDragState();

    const { source, destination, combine } = result;
    if (!destination && !combine) return;

    /** Combine/superset is not supported in month view v1. */
    if (combine) return;

    const state = store.getState();
    const { programType } = state.calendar.data;

    /** Free-form-program month view drag is not supported in v1. */
    if (programType === 'free-form-program') return;

    /** Program template dragged from the sidebar onto a month-day cell:
     * open the Load Programs modal with the drop-target date pre-filled
     * as the start date, matching day/week view's behavior. */
    if (source.droppableId === 'templates-sidebar') {
      if (!isMonthDay(destination.droppableId)) return;
      const dateKey = dateFromMonthDayId(destination.droppableId);
      const { savedWorkouts } = state.calendar.data;
      const workoutId = result.draggableId.replace('-sideBar', '');
      const draggedWorkout = savedWorkouts?.find((w) => w.id === parseInt(workoutId, 10));
      dispatch(setSelectedDate(moment(dateKey).toDate()));
      dispatch(setSelectedSavedWorkout(draggedWorkout));
      dispatch(setActiveModal('loadProgramsModal'));
      return;
    }

    /** Exercise dragged from a sidebar list onto a month-day cell. */
    if (SIDEBAR_SOURCES.includes(source.droppableId) && isMonthDay(destination.droppableId)) {
      handleSidebarDrop(result);
      return;
    }

    /** Source and destination must both be month-day cells. Anything
     * else (e.g., week-day, programmedCalendarItems) means an event
     * leaked across views — bail. */
    if (!isMonthDay(source.droppableId)) return;
    if (!isMonthDay(destination.droppableId)) return;

    const accountCode = state.auth.data.currentUser?.accountCode;
    if (!accountCode) return;

    if (source.droppableId === destination.droppableId) {
      /** Skip a true no-move drop. For a multi-select drag this check is
       * unreliable (source.index is full-array, destination.index is
       * relative to the collapsed/visible rows), so only apply it for
       * single-item drags — the multi-select branch has its own
       * order-unchanged guard. */
      const selectedItems = state.calendar.data.selectedItems || {};
      const isMulti = selectedItems[result.draggableId]
        && Object.keys(selectedItems).length > 1;
      if (!isMulti && source.index === destination.index) return;
      handleSameDayReorder(result, accountCode);
      return;
    }

    handleCrossDayMove(result, accountCode);
  }, [cleanupDragState, handleSameDayReorder, handleCrossDayMove, handleSidebarDrop]);

  /** Same reason as useDragManager: outside React's own event handlers React
   * 16 renders once per dispatch, so the multi-dispatch handlers are batched. */
  const handleDragEnd = useCallback((result) => {
    batch(() => finishDragEnd(result));
  }, [finishDragEnd]);

  const publishDragStart = useCallback((start) => {
    const { source, draggableId } = start;
    dispatch(setIsDragging(true));
    dispatch(setDragSource(source.droppableId));

    if (!isMonthDay(source.droppableId)) return;

    /** Resolve dragged item so any UI listening to setDraggedItem
     * (e.g. dragLayer indicators) has the data. */
    const state = store.getState();
    const dateKey = dateFromMonthDayId(source.droppableId);
    const items = getDayItems(state, dateKey);
    const draggedItem = items.find((i) => String(i.assignedId) === draggableId)
      || items[source.index];
    if (draggedItem) {
      dispatch(setDraggedItem({ ...draggedItem, sourceDroppableId: source.droppableId }));
    }
  }, [dispatch]);

  const handleDragStart = useCallback((start) => {
    batch(() => publishDragStart(start));
  }, [publishDragStart]);

  const handleOnBeforeCapture = useCallback((before) => {
    /** Month view is a static grid (no virtualization, no placeholder
     * height measurement). Mark drag-in-progress so any cross-cutting
     * UI hooks that listen to it stay consistent with day/week. */
    dispatch(setDragIsInProgress(true));

    /** Collapse the rows that hide during a chunk drag (the other
     * multi-selected rows, or a session break's children) BEFORE rbd
     * captures dimensions. rbd uses the captured sizes to animate the
     * sibling "nudge"; if we only collapse on drag start (after capture)
     * rbd thinks those rows are still full-height and won't open a drop
     * slot under the target when a collapsed row sits between the dragged
     * row and the target. flushSync commits the collapse synchronously so
     * rbd measures the already-collapsed layout (day/week get this via
     * onBeforeCapture + virtualization). */
    const draggableId = before?.draggableId;
    if (!draggableId) return;

    const { calendarItems, selectedItems } = store.getState().calendar.data;
    let dragged = null;
    let dateKey = null;
    const dateKeys = Object.keys(calendarItems || {});
    for (let i = 0; i < dateKeys.length; i += 1) {
      const items = calendarItems[dateKeys[i]]?.exercises || [];
      const found = items.find((it) => String(it.assignedId) === String(draggableId));
      if (found) {
        dragged = found;
        dateKey = dateKeys[i];
        break;
      }
    }
    if (!dragged) return;

    const isMultiSelect = selectedItems?.[dragged.assignedId]
      && Object.keys(selectedItems || {}).length > 1;
    const isSessionBreak = dragged.type === 'session_break';
    if (!isMultiSelect && !isSessionBreak) return;

    flushSync(() => {
      dispatch(setDraggedItem({ ...dragged, sourceDroppableId: `month-day-${dateKey}` }));
    });
  }, [dispatch]);

  return {
    handleDragEnd,
    handleDragStart,
    handleOnBeforeCapture,
  };
};

export default useMonthViewDragManager;
